import React, { useEffect, useRef, useState } from "react";
import "./StackedCards.css";

const StackedCards = () => {
  const sectionRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);

  const cards = [
    {
      id: 1,
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" stroke="currentColor" fill="none" strokeWidth="2">
          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
        </svg>
      ),
      title: "Lightning Fast",
      description: "Experience blazing fast performance with optimized code and instant loading times.",
      features: ["Instant Load", "Zero Lag", "Optimized Performance"],
      gradient: "linear-gradient(135deg, #fa7e04, #fdef7c)"
    },
    {
      id: 2,
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" stroke="currentColor" fill="none" strokeWidth="2">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
      ),
      title: "Bank-Level Security",
      description: "Your data is protected with enterprise-grade encryption and strict security protocols.",
      features: ["256-bit Encryption", "2FA Authentication", "Secure Backup"],
      gradient: "linear-gradient(135deg, #fa7e04, #fdef7c)"
    },
    {
      id: 3,
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" stroke="currentColor" fill="none" strokeWidth="2">
          <rect x="2" y="3" width="20" height="14" rx="2"/>
          <line x1="8" y1="21" x2="16" y2="21"/>
          <line x1="12" y1="17" x2="12" y2="21"/>
        </svg>
      ),
      title: "Beautiful Design",
      description: "Stunning interfaces crafted with attention to every pixel and interaction.",
      features: ["Modern UI", "Dark Mode", "Responsive Design"],
      gradient: "linear-gradient(135deg, #fa7e04, #fdef7c)"
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;

      const section = sectionRef.current;
      const sectionTop = section.offsetTop;
      const sectionHeight = section.offsetHeight;
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;

      // Calculate progress through the section
      const start = sectionTop - windowHeight * 0.3;
      const end = sectionTop + sectionHeight - windowHeight * 0.7;
      const progress = Math.max(0, Math.min(1, (scrollY - start) / (end - start)));

      setScrollProgress(progress);

      // Calculate which card should be active
      const cardProgress = progress * cards.length;
      const newActiveIndex = Math.min(Math.floor(cardProgress), cards.length - 1);
      setActiveIndex(newActiveIndex);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [cards.length]);

  return (
    <section className="stacked-cards-section" id="about" ref={sectionRef}>
      {/* Sticky Header */}
      <div className="stacked-header">
        <div className="header-content">
          <span className="stacked-badge">
            <span className="badge-shine"></span>
            Why Choose FlyFi
          </span>

          <h2 className="stacked-title">
            Everything You Need,
            <span className="gradient-text"> All in One Place.</span>
          </h2>

          <p className="stacked-description">
            Scroll to watch the cards stack into view
          </p>

          {/* Progress Indicator */}
          <div className="progress-indicator">
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{ width: `${(activeIndex + 1) / cards.length * 100}%` }}
              />
            </div>
            <span className="progress-text">
              {activeIndex + 1} of {cards.length} features shown
            </span>
          </div>
        </div>
      </div>

      {/* Sticky Cards Container */}
      <div className="cards-container-wrapper">
        <div className="cards-fixed-container">
          <div className="cards-wrapper">
            {cards.map((card, index) => {
              const cardStart = index / cards.length;
              const cardEnd = (index + 1) / cards.length;
              const cardProgress = Math.max(0, Math.min(1, (scrollProgress - cardStart) / (cardEnd - cardStart)));
              
              // Determine card states
              const isPrevious = index < activeIndex;
              const isActive = index === activeIndex;
              const isNext = index === activeIndex + 1;
              const isFuture = index > activeIndex + 1;
              
              // Calculate transforms based on state
              let translateX = 100; // Start off-screen to the right
              let scale = 0.8;
              let opacity = 0;
              let zIndex = cards.length - index;
              let top = 0;

              if (isPrevious) {
                // Previous cards: move up and scale down
                translateX = 0;
                scale = 0.9 - (activeIndex - index) * 0.1;
                opacity = 0.7;
                top = -40 * (activeIndex - index); // Stack upwards
              } else if (isActive) {
                // Active card: center stage
                translateX = (1 - cardProgress) * 100;
                scale = 0.9 + cardProgress * 0.1;
                opacity = cardProgress;
                top = 0;
                zIndex = cards.length + 10; // Highest z-index for active card
              } else if (isNext) {
                // Next card: preparing to enter
                translateX = 100;
                scale = 0.8;
                opacity = 0;
                top = 0;
              } else if (isFuture) {
                // Future cards: hidden
                translateX = 100;
                scale = 0.8;
                opacity = 0;
                top = 0;
              }

              return (
                <div
                  key={card.id}
                  className="stacked-card"
                  style={{
                    transform: `translateX(${translateX}px) scale(${scale})`,
                    opacity: opacity,
                    zIndex: zIndex,
                    top: `${top}px`,
                    '--card-gradient': card.gradient,
                    transition: 'all 0.6s cubic-bezier(0.23, 1, 0.32, 1)'
                  }}
                >
                  <div className="card-gradient-border"></div>
                  <div className="card-glow-effect"></div>

                  <div className="card-content-wrapper">
                    <div className="card-icon-wrapper">
                      <div className="card-icon">{card.icon}</div>
                      <div className="icon-glow"></div>
                    </div>

                    <div className="card-text">
                      <h3 className="card-title">{card.title}</h3>
                      <p className="card-description">{card.description}</p>
                    </div>

                    <ul className="card-features">
                      {card.features.map((feature, i) => (
                        <li key={i} className="feature-item">
                          <div className="feature-icon">
                            <svg width="20" height="20" viewBox="0 0 20 20">
                              <defs>
                                <linearGradient id={`gradient-${card.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
                                  <stop offset="0%" stopColor="#fa7e04" />
                                  <stop offset="100%" stopColor="#fdef7c" />
                                </linearGradient>
                              </defs>
                              <circle cx="10" cy="10" r="10" fill={`url(#gradient-${card.id})`} opacity="0.2" />
                              <path 
                                d="M6 10L9 13L14 7" 
                                stroke={`url(#gradient-${card.id})`} 
                                strokeWidth="2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                fill="none"
                              />
                            </svg>
                          </div>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Scroll Hint */}
      <div className="scroll-hint">
        <div className="scroll-hint-content">
          <span>Scroll to {activeIndex < cards.length - 1 ? 'reveal' : 'explore'} features</span>
          <div className="scroll-arrow down">↓</div>
        </div>
      </div>
    </section>
  );
};

export default StackedCards;